import { createUiLocalizationLoader } from '~/utils/uiLocalizationLoader'
import { isTokenizedPdfReportRoute, reportPageLanguageTag } from '~/utils/reportRoute'

const loaders = new WeakMap<object, ReturnType<typeof createUiLocalizationLoader>>()

// setup interface language and fetch translations
export function useLanguageSetup() {
  const nuxtApp = useNuxtApp()
  const i18n = nuxtApp.$i18n
  const setLocale = i18n.setLocale
  // Resolved here, while a component or plugin context is guaranteed.
  const route = useRoute()

  const authStore = useAuthStore()
  const { userContext } = storeToRefs(authStore)
  const { fetchTranslations, fetchLanguages, fetchUILocalizations } = useLanguageStore()
  const { interfaceLanguage, translations, languages }
    = storeToRefs(useLanguageStore())

  if (!loaders.has(nuxtApp)) {
    const bundledMessages = new Map<string, Record<string, unknown>>()
    loaders.set(nuxtApp, createUiLocalizationLoader(
      (locale) => {
        if (!bundledMessages.has(locale))
          bundledMessages.set(locale, { ...i18n.getLocaleMessage(locale) })
        return fetchUILocalizations(locale, undefined, true)
      },
      () => i18n.getLocaleMessage('en-US'),
      (locale, messages) => i18n.setLocaleMessage(locale, {
        ...bundledMessages.get(locale),
        ...messages,
      }),
      (locale, messages) => {
        const current = i18n.getLocaleMessage(locale) as Record<string, unknown>
        return Object.entries(messages).every(([key, value]) => current[key] === value)
      },
    ))
  }
  const loadUiMessages = loaders.get(nuxtApp)!

  const setupUserLanguage = async () => {
    const pathname = window.location.pathname
    if (pathname === '/' || pathname.startsWith('/web-report/'))
      return
    const tokenizedReport = isTokenizedPdfReportRoute(pathname, window.location.search)
    if (!tokenizedReport && !(await nuxtApp.$acquireTokenSilent())?.accessToken)
      return
    const userContextLanguage = userContext.value?.language
    await fetchLanguages(ref(false))
    // fetch languageId from query params for public report page
    const languageId = route.query.languageId as string
    const languageTag = route.query.languageTag as string
    // The project report page shows the language the report was published in
    // and merges the report's saved strings into it.
    const reportTag = reportPageLanguageTag(pathname, languageTag)
    const reportLocale = reportTag && (i18n.localeCodes?.value ?? [reportTag]).includes(reportTag as never)
      ? reportTag
      : undefined
    // English is set when userContext is not available
    const locale
      = reportLocale
        ?? userContextLanguage?.tag
        ?? languageTag
        ?? resolveFallbackLocaleCode(i18n.locales?.value)
    // setLocale reloads the bundled messages even for the active locale, which
    // would overwrite the report's saved strings once the page has merged them.
    if (!reportLocale || i18n.locale.value !== reportLocale)
      await setLocale(locale as Parameters<typeof setLocale>[0])
    if (!tokenizedReport)
      await loadUiMessages(locale, userContext.value)
    const hasReportLanguage = Boolean(languageId && languageId !== 'undefined')

    if (userContextLanguage || hasReportLanguage) {
      // On the report link flow there is no user context, so the language the
      // report was published in is the interface language.
      interfaceLanguage.value
        = userContextLanguage
          ?? languages.value.find(language => language.id === languageId)
      await fetchTranslations(ref(false), userContextLanguage?.id ?? languageId)
    }
    else {
      translations.value = new Map()
      interfaceLanguage.value = languages.value[0]
    }
  }

  // Background refresh must not reset report language or reload domain data.
  const refreshUiTranslations = async () => {
    const pathname = window.location.pathname
    if (pathname === '/' || pathname.startsWith('/web-report/')
      || isTokenizedPdfReportRoute(pathname, window.location.search)) {
      return
    }
    // An expired session must not redirect a user who is only reading a page.
    if (!(await nuxtApp.$acquireTokenSilent({ interactive: false }))?.accessToken)
      return
    await loadUiMessages(i18n.locale.value, userContext.value)
  }

  return {
    refreshUiTranslations,
    setupUserLanguage,
  }
}
